import React, { useState, useMemo, useCallback } from "react";
import * as XLSX from "xlsx";
import { Table, Button } from 'react-bootstrap';

// RTK Query hooks
import { useGetSalesQuery } from "../../slices/salesApiSlice";
import { useGetExpensesQuery } from "../../slices/expensesApiSlice";
import { useGetIncomesQuery } from "../../slices/incomesApiSlice";
import { useGetDepositsQuery } from "../../slices/depositsApiSlice";
import { useGetSuppliesQuery } from "../../slices/suppliesApiSlice";
import { useGetStocksQuery } from "../../slices/stocksApiSlice";
import { useGetTransfersQuery } from "../../slices/transfersApiSlice";

export default function StockDateExpenses() {

  const safeFixed = (value) => Number(value || 0).toFixed(2);
  const today = new Date().toISOString().split("T")[0];
  const [date1, setDate1] = useState(today);
  const [date2, setDate2] = useState(today);
  const [reportData, setReportData] = useState([]);
  const [totals, setTotals] = useState({});

  // Fetch data
  const { data: salesData, isLoading: salesLoading } = useGetSalesQuery();
  const { data: expensesData, isLoading: expensesLoading } = useGetExpensesQuery({ startDate: today, endDate: today });
  const { data: incomesData, isLoading: incomesLoading } = useGetIncomesQuery({ startDate: today, endDate: today });
  const { data: depositsData, isLoading: depositsLoading } = useGetDepositsQuery({ startDate: today, endDate: today });
  const { data: suppliesData, isLoading: suppliesLoading } = useGetSuppliesQuery();
  const { data: transfersData } = useGetTransfersQuery();
  const { data: stocksData } = useGetStocksQuery();

  const formatDate = date => new Date(date).toLocaleDateString("en-CA");
  const getDatesBetween = (start, end) => {
    const dates = [];
    let curr = new Date(start);
    const endDate = new Date(end);
    while (curr < endDate) {
      dates.push(formatDate(curr));
      curr.setDate(curr.getDate() + 1);
    }
    return dates;
  };

  const sales = useMemo(() => salesData?.sales || [], [salesData]);
  const expenses = useMemo(() => expensesData?.expenses || [], [expensesData]);
  const incomes = useMemo(() => incomesData?.incomes || [], [incomesData]);
  const deposits = useMemo(() => depositsData?.deposits || [], [depositsData]);
  const supplies = useMemo(() => suppliesData?.supplies || [], [suppliesData]);
  const transfers = useMemo(() => transfersData?.transfers || [], [transfersData]);
  const stocks = useMemo(() => stocksData?.stocks || [], [stocksData]);

  const calculateReport = () => {
    const dates = getDatesBetween(date1, date2);
    const result = dates.map(date => {
      const netSales = sales
        .filter(s => formatDate(s.date) === date)
        .reduce((sum, s) => sum + (
          s.saleItems?.reduce((sItemSum, item) =>
            sItemSum + (Number(item.price) || 0) * (Number(item.qty) || 0), 0) || 0
        ), 0);

      const netExpenses = expenses
        .filter(e => formatDate(e.date) === date)
        .reduce((sum, e) => sum + Number(e.amount || 0), 0);

      const cashCollected = incomes
        .filter(i => formatDate(i.date) === date)
        .reduce((sum, i) => sum + Number(i.amount || 0), 0);

      const depositTotal = deposits
        .filter(d => formatDate(d.date) === date)
        .reduce((sum, d) => sum + Number(d.amount || 0), 0);

      const netBank = depositTotal * -1;

      const purchases = supplies
        .filter(sup => formatDate(sup.date) === date)
        .reduce((sum, sup) => sum + Number(sup.cardCost || 0), 0) * -1;

      const netCash = netSales + netExpenses;
      const difference = cashCollected - netCash;
      const cashInHand = netCash + netBank + purchases;

      return { date, netSales, netExpenses, netCash, cashCollected, difference, netBank, purchases, cashInHand };
    });

    setReportData(result);
    const totals = result.reduce((acc, r) => ({
      totalSales: acc.totalSales + r.netSales,
      totalExpenses: acc.totalExpenses + r.netExpenses,
      totalNetCash: acc.totalNetCash + r.netCash,
      totalIncomes: acc.totalIncomes + r.cashCollected,
      totalDifference: acc.totalDifference + r.difference,
      totalDeposit: acc.totalDeposit + r.netBank,
      totalPurchases: acc.totalPurchases + r.purchases,
      totalCashInHand: acc.totalCashInHand + r.cashInHand,
    }), {
      totalSales: 0, totalExpenses: 0, totalNetCash: 0,
      totalIncomes: 0, totalDifference: 0,
      totalDeposit: 0, totalPurchases: 0,
      totalCashInHand: 0
    });

    setTotals(totals);
  };

  const calculateStockValue = useCallback((targetDate) => {
    return stocks
      .filter(s => formatDate(s.date) === formatDate(targetDate))
      .flatMap(s => s.stockItems || [])
      .reduce((sum, item) => sum + Number(item.price || 0) * Number(item.qty || 0), 0);
  }, [stocks]);

  const calculateSupplyValue = useCallback((start, end) => {
    return supplies
      .filter(s => formatDate(s.date) >= formatDate(start) && formatDate(s.date) < formatDate(end))
      .flatMap(s => s.supplyItems || [])
      .reduce((sum, item) => sum + Number(item.price || 0) * Number(item.qty || 0), 0);
  }, [supplies]);

  const calculateTransferValue = useCallback((start, end) => {
    return transfers
      .filter(t => new Date(t.date) >= new Date(start) && new Date(t.date) < new Date(end))
      .flatMap(t => t.transferItems || [])
      .reduce((sum, item) => sum + Number(item.price || 0) * Number(item.qty || 0), 0);
  }, [transfers]);

  const calculateExpenseValue = useCallback((start, end) => {
    return expenses
      .filter(e => new Date(e.date) >= new Date(start) && new Date(e.date) < new Date(end))
      .reduce((sum, e) => sum + Number(e.amount || 0), 0);
  }, [expenses]);


  const calculateIncomeValue = useCallback((start, end) => {
    return incomes
      .filter(i => new Date(i.date) >= new Date(start) && new Date(i.date) < new Date(end))
      .reduce((sum, i) => sum + Number(i.amount || 0), 0);
  }, [incomes]);

  // Function to calculate only SalesCredit-related expenses within the date range
    const calculateSalesCreditExpenseValue = (start, end) => {
      return expensesData?.expenses
        ?.filter(expense => {
          const expenseDate = new Date(expense.date).toISOString().split('T')[0];
          return expense.category === "SalesCredit" && expenseDate >= start && expenseDate < end;
        })
        .reduce((sum, expense) => sum + Number(expense.amount || 0), 0) || 0;
    };
  
    const calculateSalesPaymentExpenseValue = (start, end) => {
    return expensesData?.expenses
      ?.filter(expense => {
        const expenseDate = new Date(expense.date).toISOString().split('T')[0];
        return expense.category === "SalesPayment" && expenseDate >= start && expenseDate < end;
      })
      .reduce((sum, expense) => sum + Number(expense.amount || 0), 0) || 0;
  };
  

  const openingStock = calculateStockValue(date1);
  const closingStock = calculateStockValue(date2);
  const supplyVal = calculateSupplyValue(date1, date2);
  const transferVal = calculateTransferValue(date1, date2);
  const expenseVal = calculateExpenseValue(date1, date2);
  const incomeVal = calculateIncomeValue(date1, date2);
  const netValue = openingStock + supplyVal + expenseVal - transferVal - closingStock;
  const variation = incomeVal - netValue;

  const expenseSalesCreditTotal = calculateSalesCreditExpenseValue(date1, date2);
  
  const expenseSalesPaymentTotal = calculateSalesPaymentExpenseValue(date1, date2);
       const increaseInReceivables = expenseSalesCreditTotal + expenseSalesPaymentTotal;
       const reValue = increaseInReceivables < 0 
    ? variation - increaseInReceivables 
    : variation;
  

  const exportToExcel = () => {
    const exportData = reportData.map(row => ({
      Date: row.date,
      NetSales: row.netSales.toFixed(2),
      NetExpenses: row.netExpenses.toFixed(2),
      NetCash: row.netCash.toFixed(2),
      CashCollected: row.cashCollected.toFixed(2),
      Difference: row.difference.toFixed(2),
      NetBank: row.netBank.toFixed(2),
      Purchases: row.purchases.toFixed(2),
      CashInHand: row.cashInHand.toFixed(2),
    }));
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Balance Report");
    XLSX.writeFile(workbook, "BalanceReport.xlsx");
  };
    
  
  
  return (
    <>
      <div className="balance-report">
        <div className="date-range">
          <label>Start Date:
            <input type="date" value={date1} onChange={e => setDate1(e.target.value)} />
          </label>
          <label>End Date:
            <input type="date" value={date2} onChange={e => setDate2(e.target.value)} />
          </label>
        </div>
        <button onClick={calculateReport} className="calculate-button">
          Calculate Report
        </button>

        {salesLoading || expensesLoading || incomesLoading || depositsLoading || suppliesLoading ? (
          <div>Loading data...</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Net Sales</th>
                <th>Net Expenses</th>
                <th>Net Cash</th>
                <th>Cash Collected</th>
                <th>Difference</th>
                <th>Net Bank</th>
                <th>Purchases</th>
                <th>Cash In Hand</th>
              </tr>
            </thead>
            <tbody>
              {reportData.map(row => (
                <tr key={row.date}>
                  <td>{row.date}</td>
                  <td>{row.netSales.toFixed(2)}</td>
                  <td>{row.netExpenses.toFixed(2)}</td>
                  <td>{row.netCash.toFixed(2)}</td>
                  <td>{row.cashCollected.toFixed(2)}</td>
                  <td>{row.difference.toFixed(2)}</td>
                  <td>{row.netBank.toFixed(2)}</td>
                  <td>{row.purchases.toFixed(2)}</td>
                  <td>{row.cashInHand.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td><strong>Total</strong></td>
                
                <td><strong>{safeFixed(totals.totalSales)}</strong></td>
                <td><strong>{safeFixed(totals.totalExpenses)}</strong></td>
                <td><strong>{safeFixed(totals.totalNetCash)}</strong></td>
                <td><strong>{safeFixed(totals.totalIncomes)}</strong></td>
                <td><strong>{safeFixed(totals.totalDifference)}</strong></td>
                <td><strong>{safeFixed(totals.totalDeposit)}</strong></td>
                <td><strong>{safeFixed(totals.totalPurchases)}</strong></td>
                <td><strong>{safeFixed(totals.totalCashInHand)}</strong></td>

              </tr>
            </tfoot>
          </table>
        )}

        <button onClick={exportToExcel} className="export-button">
          Export to Excel
        </button>
      </div>

      <h2>Working Capital Summary</h2>

      <Button onClick={calculateReport} variant="primary" className="mb-3">
        Calculate Stock Values
      </Button>

      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th>Date1</th>
            <th>Date2</th>
            <th>Opening Stock</th>
            <th>Supply Value</th>
            <th>Transfer Value</th>
            <th>Expense Value</th>
            <th>Closing Stock</th>
            <th>Net Value</th>
            <th>Income Value</th>
            <th>Variation</th>
            <th style={{ display: 'none' }}>ReVar</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{date1}</td>
            <td>{date2}</td>
            <td>{openingStock.toFixed(2)}</td>
            <td>{supplyVal.toFixed(2)}</td>
            <td>{transferVal.toFixed(2)}</td>
            <td>{expenseVal.toFixed(2)}</td>
            <td>{closingStock.toFixed(2)}</td>
            <td>{netValue.toFixed(2)}</td>
            <td>{incomeVal.toFixed(2)}</td>
            <td>{variation.toFixed(2)}</td>
            <td style={{ display: 'none' }}>{reValue.toFixed(2)}</td>
          </tr>
        </tbody>
      </Table>
    </>
  );
}